import React, { useState, useEffect } from 'react';
import { Modal, Spinner, Row, Col, Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';

/**
 * Resolves a procurement request via internal transfer: search existing inventory, select the
 * asset(s) to move to the request's target location, confirm the transfer type + dates.
 *
 * Props: isOpen, toggle, request, onResolve(payload), isSubmitting, apiError
 */
const TransferResolutionModal = ({ isOpen, toggle, request, onResolve, isSubmitting = false, apiError = '' }) => {
  const { t } = useTranslation();
  const currentUser = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = currentUser?.activeOrganizationId ?? null;

  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [temporary, setTemporary] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTerm(''); setResults([]); setSelected([]); setReason(''); setError('');
      setTemporary(request?.usageType === 'TEMPORARY');
      setStartDate(request?.startDate || '');
      setEndDate(request?.endDate || '');
    }
  }, [isOpen, request]);

  // Debounced asset search
  useEffect(() => {
    if (!isOpen || !organizationId || term.trim().length === 0) { setResults([]); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get('asset/all', { params: { organizationId, count: 8, search: term, hierarchy: false } });
        setResults(data?.response?.[0]?.assets || []);
      } catch (e) { console.error('asset search error', e); }
    }, 300);
    return () => clearTimeout(h);
  }, [term, isOpen, organizationId]);

  // A transfer moves the asset's WHOLE subtree — fetch the descendant count so the
  // admin sees "moves with N sub-assets" on the chip before confirming.
  const addAsset = async (a) => {
    if (!selected.some((s) => s.id === a.id)) {
      setSelected((p) => [...p, { id: a.id, label: `${a.assetName}${a.serialNumber ? ` (${a.serialNumber})` : ''}`, subAssets: 0 }]);
      try {
        const { data } = await api.get(`asset/descendant-count?assetId=${a.id}`);
        const n = Number(data?.response) || 0;
        if (n > 0) setSelected((p) => p.map((s) => (s.id === a.id ? { ...s, subAssets: n } : s)));
      } catch (e) { console.error('descendant count error', e); }
    }
    setTerm(''); setResults([]);
  };
  const removeAsset = (id) => setSelected((p) => p.filter((s) => s.id !== id));

  const handleSubmit = () => {
    if (selected.length === 0) { setError(t('planning_purchasing.select_asset_required', 'Select at least one asset to transfer')); return; }
    if (temporary && (!startDate || !endDate)) { setError(t('planning_purchasing.transfer_dates_required', 'Start and end dates are required for a temporary transfer')); return; }
    if (temporary && endDate < startDate) { setError(t('planning_purchasing.end_before_start', 'End date must be on or after the start date')); return; }
    onResolve({
      requestId: request.id,
      assetIds: selected.map((s) => s.id),
      temporary,
      startDate: temporary ? (startDate || null) : null,
      endDate: temporary ? (endDate || null) : null,
      reason: reason || null,
    });
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}>
        <div className="modal-header">
          <h5 className="modal-title">{t('planning_purchasing.resolve_transfer', 'Resolve via Internal Transfer')}</h5>
          <button type="button" className="btn-close" onClick={toggle} />
        </div>
        <div className="modal-body">
          {(apiError || error) && <Alert color={apiError ? 'danger' : 'warning'}>{apiError || error}</Alert>}
          <p className="text-muted small mb-2">
            {t('planning_purchasing.transfer_to', 'The selected asset(s) will be moved to')}{' '}
            <strong>{request?.locationName || t('shared.location', 'the target location')}</strong>{' '}
            {t('planning_purchasing.to_fulfil', 'to fulfil')} <strong>{request?.requestNumber}</strong>.
          </p>
          <p className="small mb-3">
            {t('planning_purchasing.requested_qty', 'Requested quantity')}:{' '}
            <strong>{request?.quantity}{request?.unit ? ` ${request.unit}` : ''}</strong>
            <span className="text-muted"> · {selected.length} {t('planning_purchasing.selected', 'selected')}</span>
          </p>

          {/* Asset search */}
          <label className="form-label fw-medium">{t('planning_purchasing.search_assets', 'Search inventory assets')}</label>
          <div className="position-relative mb-2">
            <input className="form-control" value={term} onChange={(e) => setTerm(e.target.value)}
                   placeholder={t('shared.type_to_search_assets', 'Type to search assets...')} />
            {results.length > 0 && (
              <div className="position-absolute w-100 bg-white border rounded-bottom shadow-sm" style={{ zIndex: 1051, maxHeight: 200, overflowY: 'auto', top: '100%' }}>
                {results.map((a) => (
                  <div key={a.id} className="p-2 border-bottom" style={{ cursor: 'pointer' }} onClick={() => addAsset(a)}>
                    <div className="fw-medium">{a.assetName}</div>
                    <small className="text-muted">{a.serialNumber} · {a.currentLocation}</small>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selected.length > 0 && (
            <>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {selected.map((s) => (
                  <span key={s.id} className="badge bg-light text-dark border d-flex align-items-center gap-1 p-2">
                    {s.label}
                    {s.subAssets > 0 && (
                      <span className="text-muted fw-normal">
                        (+{s.subAssets} {t('planning_purchasing.sub_assets', 'sub-assets')})
                      </span>
                    )}
                    <FeatherIcon icon="x" size={14} style={{ cursor: 'pointer' }} onClick={() => removeAsset(s.id)} />
                  </span>
                ))}
              </div>
              {selected.some((s) => s.subAssets > 0) && (
                <small className="text-muted d-block mb-3">
                  <FeatherIcon icon="git-branch" size={12} className="me-1" />
                  {t('planning_purchasing.sub_assets_note', 'Sub-assets always move together with their parent asset.')}
                </small>
              )}
            </>
          )}

          {/* Transfer type */}
          <Row className="mb-2">
            <Col md={4}>
              <label className="form-label fw-medium">{t('planning_purchasing.transfer_type', 'Transfer Type')}</label>
              <div className="d-flex gap-3 pt-1">
                <div className="form-check">
                  <input className="form-check-input" type="radio" id="tt-perm" checked={!temporary} onChange={() => setTemporary(false)} />
                  <label className="form-check-label" htmlFor="tt-perm">{t('planning_purchasing.usage_permanent', 'Permanent')}</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" id="tt-temp" checked={temporary} onChange={() => setTemporary(true)} />
                  <label className="form-check-label" htmlFor="tt-temp">{t('planning_purchasing.usage_temporary', 'Temporary')}</label>
                </div>
              </div>
            </Col>
            {temporary && (
              <>
                <Col md={4}>
                  <label className="form-label fw-medium">{t('planning_purchasing.start_date', 'Start Date')} <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </Col>
                <Col md={4}>
                  <label className="form-label fw-medium">{t('planning_purchasing.end_date', 'End Date')} <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
                </Col>
              </>
            )}
          </Row>
          {temporary && (
            <small className="text-muted d-block mb-2"><FeatherIcon icon="info" size={12} className="me-1" />
              {t('planning_purchasing.temp_transfer_hint', 'A return reminder is scheduled automatically for the end date.')}</small>
          )}
          {temporary && startDate && startDate > new Date().toISOString().slice(0, 10) && (
            <small className="text-info d-block mb-2"><FeatherIcon icon="clock" size={12} className="me-1" />
              {t('planning_purchasing.transfer_scheduled_note', 'The move will execute on the start date — the asset stays where it is until then.')}</small>
          )}

          <label className="form-label fw-medium">{t('planning_purchasing.reason', 'Reason')}</label>
          <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder={t('planning_purchasing.transfer_reason_ph', 'Optional reason for the transfer')} />
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-secondary" onClick={toggle}>{t('shared.cancel', 'Cancel')}</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? <><Spinner size="sm" className="me-2" />{t('planning_purchasing.transferring', 'Transferring...')}</>
              : t('planning_purchasing.confirm_transfer', 'Confirm Transfer')}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default TransferResolutionModal;
